import { Component } from '@theme/component';
import { fetchConfig, debounce, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** Batched line-item removal state — see {@link onLineItemRemove}. */
  /** @type {Set<string>} */
  #pendingRemovalKeys = new Set();
  /** @type {HTMLElement[]} */
  #pendingRemovalRows = [];
  /** @type {ReturnType<typeof setTimeout> | null} */
  #removalTimer = null;
  /** @type {Promise<unknown>} */
  #removalChain = Promise.resolve();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line, isBundle } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line, event);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
      isBundle
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles a line item removal.
   *
   * Removals show a pending spinner on the affected rows (addressed by the line's stable key) and
   * are coalesced into a single /cart/update.js call; the section morph from the response removes
   * the rows. The old path froze the whole cart (pointer-events: none) for a full round-trip per
   * click and addressed lines by index, so removing items quickly both lagged and raced (the Cart
   * Transform re-keys every line on each mutation).
   *
   * @param {number} line - The line item index (1-based; from the on:click binding).
   * @param {Event} [event] - The originating event, when the removal came from a row control.
   */
  onLineItemRemove(line, event) {
    // Rows render in a custom (timestamp-sorted) order, so cartItemRows[line - 1] is the clicked
    // row only by coincidence. Resolve the actual row from the event target.
    const eventTarget = event?.target;
    const clickedRow = eventTarget instanceof Element ? eventTarget.closest('.cart-items__table-row') : null;
    const row = clickedRow instanceof HTMLElement ? clickedRow : this.refs.cartItemRows[line - 1];

    if (!(row instanceof HTMLElement)) return;

    const key = row.dataset.key;
    // Without a stable line key we can't batch by key — fall back to the single-line path.
    if (!key) {
      this.updateQuantity({ line, quantity: 0, action: 'clear', isBundle: false });
      this.#markRowsPending([row]);
      return;
    }

    const rows = [row, ...this.refs.cartItemRows.filter((r) => r.dataset.parentKey === key)];
    this.#markRowsPending(rows);
    this.#queueLineRemoval(key, rows);
  }

  /**
   * Marks rows as pending removal: the row stays in place, its trash icon becomes a spinner (styled
   * in cart-products.css / cart-products--drawer.css) and pointer events are blocked so a second
   * click can't fire mid-request. The section morph from the server response removes the rows on
   * success; {@link #restoreRows} clears the state if the request fails.
   * @param {HTMLElement[]} rows
   */
  #markRowsPending(rows) {
    for (const row of rows) {
      row.classList.add('cart-items__table-row--pending-removal');
      row.setAttribute('aria-busy', 'true');
    }
  }

  /** @param {HTMLElement[]} rows */
  #restoreRows(rows) {
    for (const row of rows) {
      row.classList.remove('cart-items__table-row--pending-removal');
      row.removeAttribute('aria-busy');
    }
  }

  /**
   * Queues a line for removal, coalescing a burst of clicks into one debounced batch.
   * @param {string} key
   * @param {HTMLElement[]} rows
   */
  #queueLineRemoval(key, rows) {
    this.#pendingRemovalKeys.add(key);
    this.#pendingRemovalRows.push(...rows);
    if (this.#removalTimer) clearTimeout(this.#removalTimer);
    this.#removalTimer = setTimeout(() => this.#flushLineRemovals(), 250);
  }

  #flushLineRemovals() {
    this.#removalTimer = null;
    if (!this.#pendingRemovalKeys.size) return;

    const keys = [...this.#pendingRemovalKeys];
    const rows = this.#pendingRemovalRows;
    this.#pendingRemovalKeys = new Set();
    this.#pendingRemovalRows = [];

    // Serialize behind any in-flight batch so each request commits before the next is sent.
    this.#removalChain = this.#removalChain.then(() => this.#writeLineRemovals(keys, rows));
  }

  /**
   * Removes the batched keys in one /cart/update.js call and morphs from the response.
   * @param {string[]} keys
   * @param {HTMLElement[]} rows
   */
  async #writeLineRemovals(keys, rows) {
    const marker = cartPerformance.createStartingMarker('clear:user-action');
    this.refs.cartTotal?.shimmer();

    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionsToUpdate.add(item.dataset.sectionId);
    });

    /** @type {Record<string, number>} */
    const updates = {};
    for (const key of keys) updates[key] = 0;

    try {
      const response = await fetch(
        `${Theme.routes.cart_update_url}`,
        fetchConfig('json', {
          body: JSON.stringify({
            updates,
            sections: Array.from(sectionsToUpdate),
            sections_url: window.location.pathname,
          }),
        })
      );
      const data = await response.json();
      if (data.errors) throw new Error(typeof data.errors === 'string' ? data.errors : 'Cart update failed');

      const newSectionHTML = new DOMParser().parseFromString(data.sections[this.sectionId], 'text/html');
      const hiddenCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const itemCount = hiddenCount ? parseInt(hiddenCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount,
          source: 'cart-items-component',
          sections: data.sections,
        })
      );

      morphSection(this.sectionId, data.sections[this.sectionId]);
    } catch (error) {
      this.#restoreRows(rows); // bring the rows back so the UI matches the unchanged cart
      console.error(error);
    } finally {
      resetShimmer(this);
      cartPerformance.measureFromMarker(marker);
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {boolean} config.isBundle - Check for Bundle
   */
  updateQuantity(config) {
    if (config.isBundle) return;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
